/**
 * @file ワンタイムパスワード入力ページ
 *
 * SMSで送信されたワンタイムパスワードを入力・検証し、
 * 成功時はパスワード再設定ページへ遷移する。
 */
import { yupResolver } from '@hookform/resolvers/yup';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { userState } from '@/states/selectors/userState';
import * as yup from 'yup';

import api from '@/utils/api';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';

import useSetLoading from '@/hooks/useSetLoading';
import { alertState } from '@/states/alertState';
import { OnetimePasswordForm, onetimePasswordFormState } from '@/states/onetimePasswordFormState';
import { providerInfoState } from '@/states/providerInfoState';

import LinkButton from '@/components/atoms/LinkButton';
import SystemErrorMessage from '@/components/atoms/SystemErrorMessage';
import MlLabeledTextBox from '@/components/atoms/molecules/MlLabeledTextBox';

const OnetimePassword: NextPage = () => {
  const router = useRouter();
  const [onetimePasswordForm, setOnetimePasswordForm] = useRecoilState(onetimePasswordFormState);
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const { startLoading, finishLoading } = useSetLoading();
  const user = useRecoilValue(userState);
  const provider = useRecoilValue(providerInfoState);
  const setAlert = useSetRecoilState(alertState);

  const schema = yup.object().shape({
    onetimePassword: yup.string().required('ワンタイムパスワードを入力してください。').max(32, 'ワンタイムパスワードは32文字以内で入力してください。'),
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<OnetimePasswordForm>({
    defaultValues: onetimePasswordForm,
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // セキュリティのため、画面表示時にワンタイムパスワード入力欄を必ず空にする（ブラウザバック時の残留防止）
  useEffect(() => {
    setValue('onetimePassword', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ワンタイムパスワードをAPIで検証する。成功時はloginIdを保存してパスワード再設定画面へ遷移する
  const isValid = async (): Promise<void> => {
    startLoading(ApiUrl.NonProtected.VALIDATE_ONETIME_PASSWORD);
    await api
      .post(ApiUrl.NonProtected.VALIDATE_ONETIME_PASSWORD, { onetime_password: getValues('onetimePassword') }, { headers: { 'content-type': 'application/json' } })
      .then((res) => {
        // 検証成功: APIレスポンスからloginIdを取得し、次画面で表示するために保存する
        setOnetimePasswordForm({
          loginId: res.data.login_id,
          onetimePassword: getValues('onetimePassword'),
        });

        router.push('/password-reset/resetting');
      })
      .catch((error) => {
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        const errorCode = error.response.data?.error_code;

        // VALIDATION_NG: 入力されたワンタイムパスワードが不一致（期限切れ含む）
        if (errorCode === 'VALIDATION_NG') {
          setLocalErrorMessage('ワンタイムパスワードが間違っています。');
        } else {
          setAlert({
            isOpen: true,
            apiType: '',
          });
        }
      })
      .finally(() => finishLoading(ApiUrl.NonProtected.VALIDATE_ONETIME_PASSWORD));
  };

  // フォームバリデーション（yup）でNGの場合のハンドラ（API呼び出し前にクライアント側でブロック）
  const isInValid = () => {
    setLocalErrorMessage('ワンタイムパスワードが正しくありません。');
  };

  return (
    <main>
      <div className='form-area'>
        <p className='left mb15'>
          連絡先電話番号宛にワンタイムパスワードを送信しました。
          <br />
          SMSに記載されたワンタイムパスワードを入力してください。
        </p>

        {localErrorMessage && <SystemErrorMessage message={localErrorMessage} />}

        <dl className='row1'>
          <MlLabeledTextBox type='text' id='onetimePassword' dtClassName='taLeft' register={register('onetimePassword')} error={errors.onetimePassword}>
            ワンタイムパスワード
          </MlLabeledTextBox>
        </dl>

        <LinkButton onClick={handleSubmit(isValid, isInValid)} className='login green button'>
          確認
        </LinkButton>

        <p className='left mb15'>
          ■SMSが届かない場合
          <br />
          入力された連絡先電話番号が、登録された連絡先電話番号と異なる可能性がございます。
          <br />
          お心当たりのある別の連絡先電話番号にて、再度お試しいただくか、
          <br />
          下記連絡先までお電話くださいますよう、よろしくお願いいたします。
          <br />
          <br />
          {/* SNCCとFVNOで問い合わせ先が異なるため、事業者種別に応じて表示を切り替える */}
          {user.isSncc && (
            <span>
              NURO 光 Connect サポートデスク
              <br />
              0570-020-170&emsp;または&emsp;03-6705-5836
              <br />
              受付時間：10:00～18:00
              <br />
              1⽉1⽇、2⽇および弊社指定のメンテナンス⽇を除く
            </span>
          )}
          {user.isFvno && (
            <span>
             {provider.supportdeskname}
             <br />
             {provider.supportdesktel}
             <br />
              受付時間：{provider.supportdeskemploymenttime}
              <br />
              {provider.supportdeskemploymentday}
            </span>
          )}
        </p>
      </div>
    </main>
  );
};

export default OnetimePassword;
